import React, { useEffect, useId, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { actions as messagesActions } from "../../../../actions/messages";
import { actions as profileActions } from "../../../../actions/account/profile";
import { buildAttentionRows } from "../../../../helpers/needsAttention";
import styles from "./needsAttention.module.css";

/**
 * The My Feed "Needs your attention" card (GROWTH-7133): up to two rows, each a link out to where the member can act.
 * It reads the counts from the store and asks the server for fresh ones when it opens, since the profile is loaded
 * once per session. Renders nothing when no row has anything to say.
 */

// The row icons are inline SVG, not .svg imports: every Jest suite that renders the Feed page would otherwise have to
// mock them. They are decorative (aria-hidden), so each link's name stays its text. Strokes take currentColor.
const ICON_PROPS = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  focusable: "false",
  "aria-hidden": "true"
};

// Paper plane, for unread messages.
const MessagesIcon = () => (
  <svg {...ICON_PROPS} className={styles.icon}>
    <path d="M21 3 10 14" />
    <path d="M21 3 14.5 21l-4.5-7-7-4.5L21 3Z" />
  </svg>
);

// An arrow leaving a box, for finishing the profile.
const ProfileIcon = () => (
  <svg {...ICON_PROPS} className={styles.icon}>
    <path d="M10 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h5" />
    <path d="M14 8l4 4-4 4" />
    <path d="M18 12H9" />
  </svg>
);

const ArrowIcon = () => (
  <svg {...ICON_PROPS} width={16} height={16} className={styles.arrow}>
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);
const NeedsAttention = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const titleId = useId();
  const newMessages = useSelector(state => state.account.profile?.new_messages);
  const profileCompletion = useSelector(state => state.account.profile?.profile_completion);

  useEffect(() => {
    dispatch(messagesActions.getInboxCounts());
    dispatch(profileActions.getProfileCompletion());
  }, [dispatch]);

  const rows = useMemo(() => buildAttentionRows({ newMessages, profileCompletion }), [newMessages, profileCompletion]);

  if (!rows.length) {
    return null;
  }

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <h2 id={titleId} className={styles.title}>
        {t("dashboard_feed_attention_title")}
      </h2>
      <ul className={styles.list}>
        {rows.map(row =>
          row.key === "messages" ? (
            <li key={row.key}>
              <Link to="/inbox" className={styles.row}>
                <MessagesIcon />
                <span className={styles.rowText}>
                  <span className={styles.rowTitle}>{t("dashboard_feed_attention_messages", { count: row.count })}</span>
                </span>
                <ArrowIcon />
              </Link>
            </li>
          ) : (
            <li key={row.key}>
              <Link to="/profile" className={styles.row}>
                <ProfileIcon />
                <span className={styles.rowText}>
                  <span className={styles.rowTitle}>{t("dashboard_feed_attention_profile", { score: row.score })}</span>
                  <span className={styles.rowHint}>{t(`dashboard_feed_attention_field_${row.field}`)}</span>
                </span>
                <ArrowIcon />
              </Link>
            </li>
          )
        )}
      </ul>
    </section>
  );
};

export default NeedsAttention;
